import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  PresignedUploadUrlInputSchema,
  PresignedUploadUrlResponseSchema,
} from "../shared/presignedUpload";

/**
 * The PCSC Masterlist: the crossing between a vendor's own grouping codes and
 * Rexel's price class / subclass.
 *
 * Exactly one masterlist is in force. Uploading a newer workbook replaces the
 * crossing outright rather than adding a version, because two crossings in
 * flight is a question nobody wants to answer while checking a SPA.
 *
 * Bytes never pass through a handler, matching the EDI guideline flow:
 * `getUploadUrl` mints a presigned staging URL, the browser PUTs the workbook
 * straight to blob storage, and `confirmUpload` claims it — which is also where
 * the workbook is read, because that is the first request on the server that
 * can see it at all.
 */

export const ConfirmMasterlistUploadInputSchema = z.object({
  /** The staging key `getUploadUrl` answered with, its blob now uploaded. */
  uploadKey: z.string().min(1),
  fileName: z.string().min(1).max(255),
});

/**
 * What the page reports about the crossing in force.
 *
 * Counts rather than rows: the workbook states thousands of crossings and the
 * page's job is to say what is loaded, not to list it.
 */
export const MasterlistSummarySchema = z.object({
  fileName: z.string(),
  uploadedAt: z.coerce.date(),
  /** Display name of whoever uploaded it, when we can resolve one. */
  uploadedBy: z.string().nullable(),
  /** Total crossings stored. */
  rowCount: z.number().int(),
  /** Worksheets that contributed at least one crossing. */
  tabCount: z.number().int(),
  /** Distinct vendors represented. */
  vendorCount: z.number().int(),
  /**
   * Vendors whose tabs in this workbook yielded no hierarchy codes — only
   * price classes. The workbook still loaded; these vendors' codes will not
   * resolve until a workbook that reads at their levels replaces it.
   */
  degradedVendors: z.array(z.string()),
  /**
   * Codes the workbook stated more than once, at one level, with different
   * price classes. Resolved deterministically, but the contradiction is the
   * workbook's and someone should look at it.
   */
  conflicts: z.array(z.string()),
  /**
   * Rows the workbook states that are not price classes — a footnote typed
   * into the column, a formula that ran into its neighbour. Dropped on read;
   * named here because the fix is in the workbook, not in the code.
   */
  malformedRows: z.array(z.string()),
});

export type MasterlistSummary = z.infer<typeof MasterlistSummarySchema>;

export const masterlistContracts = {
  /** Mint a presigned URL for the browser to PUT the workbook to. */
  getUploadUrl: oc
    .input(PresignedUploadUrlInputSchema)
    .output(PresignedUploadUrlResponseSchema)
    .route({
      summary: "Get upload URL for the price class masterlist",
      description:
        "Returns a presigned URL for uploading the masterlist workbook that crosses vendor grouping codes to price class and subclass.",
    }),

  /**
   * Claim the staged workbook: read it, and replace the crossing in force.
   * Answers with what is now loaded.
   */
  confirmUpload: oc
    .input(ConfirmMasterlistUploadInputSchema)
    .output(MasterlistSummarySchema)
    .route({
      summary: "Load an uploaded price class masterlist",
      description:
        "Reads the uploaded masterlist workbook and replaces the crossing in force; returns row, tab and vendor counts plus degraded vendors, conflicts, and malformed rows.",
    }),

  /** What is loaded right now, or null before any workbook is uploaded. */
  getCurrent: oc
    .input(z.object({}))
    .output(z.object({ masterlist: MasterlistSummarySchema.nullable() }))
    .route({
      method: "GET",
      summary: "Get the current price class masterlist",
      description:
        "Returns a summary of the vendor-code to price class masterlist in force (counts, conflicts, degraded vendors), or null before any workbook is uploaded.",
    }),
} as const;
